import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type { Store } from '@warp-drive/core';
import type Notifications from '../services/notifications';
import type { Tag } from '../schemas/tag.ts';
import { addTagToUserItem, removeTagFromUserItem } from '../builders/profile';
import AdaptiveMenu from './adaptive-menu';
import Icon from './icon';
import TagIcon from '~icons/lucide/tag';
import PlusIcon from '~icons/lucide/plus';

interface TagPickerSignature {
  Args: {
    userItemId: string | null;
    tags: Tag[];
    assignedTagIds?: string[];
    onTagsChanged?: () => void;
  };
}

export default class TagPicker extends Component<TagPickerSignature> {
  @service declare store: Store;
  @service declare notifications: Notifications;

  @tracked newTagLabel = '';
  @tracked isSubmitting = false;

  @action
  updateNewTag(event: Event) {
    this.newTagLabel = (event.target as HTMLInputElement).value;
  }

  @action
  async addExistingTag(tagId: string) {
    if (!this.args.userItemId) return;
    this.isSubmitting = true;
    try {
      await this.store.request(
        addTagToUserItem(this.args.userItemId, { tagIds: [tagId] })
      );
      this.args.onTagsChanged?.();
    } catch {
      this.notifications.error('Failed to add tag');
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  async addNewTag() {
    const label = this.newTagLabel.trim();
    if (!label || !this.args.userItemId) return;

    this.isSubmitting = true;
    try {
      await this.store.request(
        addTagToUserItem(this.args.userItemId, { label })
      );
      this.newTagLabel = '';
      this.args.onTagsChanged?.();
    } catch {
      this.notifications.error('Failed to create tag');
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  async removeTag(tagId: string) {
    if (!this.args.userItemId) return;
    this.isSubmitting = true;
    try {
      await this.store.request(
        removeTagFromUserItem(this.args.userItemId, tagId)
      );
      this.args.onTagsChanged?.();
    } catch {
      this.notifications.error('Failed to remove tag');
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      void this.addNewTag();
    }
  }

  @action
  resetInput() {
    this.newTagLabel = '';
  }

  <template>
    <AdaptiveMenu
      @placement="dropdown-end"
      @title="Manage Tags"
      @contentClass="w-56 p-3"
      @onClose={{this.resetInput}}
    >
      <:trigger as |toggle|>
        <button
          type="button"
          class="btn btn-xs btn-ghost"
          title="Manage tags"
          {{on "click" toggle}}
        >
          <Icon @icon={{TagIcon}} class="h-3 w-3" />
        </button>
      </:trigger>
      <:content>
        {{! Existing tags }}
        {{#if @tags.length}}
          <div class="mb-2 flex flex-wrap gap-1">
            {{#each @tags as |tag|}}
              <button
                type="button"
                class="badge badge-outline badge-sm cursor-pointer gap-1"
                disabled={{this.isSubmitting}}
                {{on "click" (fn this.addExistingTag tag.id)}}
              >
                {{tag.label}}
                <Icon @icon={{PlusIcon}} class="h-2.5 w-2.5" />
              </button>
            {{/each}}
          </div>
        {{/if}}

        {{! New tag input }}
        <div class="flex gap-1">
          <input
            type="text"
            class="input input-bordered input-xs flex-1"
            placeholder="New tag..."
            aria-label="New tag name"
            value={{this.newTagLabel}}
            {{on "input" this.updateNewTag}}
            {{on "keydown" this.handleKeydown}}
          />
          <button
            type="button"
            class="btn btn-xs btn-primary"
            disabled={{this.isSubmitting}}
            {{on "click" this.addNewTag}}
          >
            Add
          </button>
        </div>
      </:content>
    </AdaptiveMenu>
  </template>
}
